import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardDocumentIcon, ShareIcon, TrashIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import useApi from '../../hooks/useApi';
import ProfileDropdown from '../../components/profileDropDown';
import NativeAffiliateBanner from '../../components/ads/NativeAffiliateBanner';
import usePageSeo from '../../utils/usePageSeo';

function isPublishedBook(book) {
  return book.publication_status === 'PB';
}

/** Last written page = `poppyruz_pages_count` (1-based `page_order_no`). */
function lastWrittenPageOrder(book) {
  const n = parseInt(String(book.poppyruz_pages_count ?? '0'), 10);
  if (Number.isNaN(n) || n < 1) return 1;
  return n;
}

function draftWriteUrl(book) {
  const page = lastWrittenPageOrder(book);
  return `/author/write/${book.book_meta_unique_id}?page=${page}`;
}

const startNewBookButtonClass =
  'inline-flex items-center px-4 py-2 rounded-lg bg-violet-900 text-white text-base font-medium hover:bg-violet-950 whitespace-nowrap';

const authorMobileIconBtn =
  'inline-flex shrink-0 items-center justify-center rounded border border-gray-200 p-2 text-gray-700 transition hover:bg-gray-50';

function publishedBookShareUrl(bookId) {
  return `${window.location.origin}/read_authored/${bookId}`;
}

function AuthorBookCard({ book, published, onOpen, onDeleteRequest, onShareRequest }) {
  return (
    <div className="flex-none shrink-0 w-40 snap-start flex flex-col items-center group overflow-visible">
      <div className="relative w-40 shrink-0 overflow-visible">
        <button
          type="button"
          onClick={onOpen}
          className="block w-full cursor-pointer text-left"
        >
          <img
            src={book.book_cover}
            alt={book.book_name}
            className="w-40 h-56 object-cover rounded-md shadow-md transition duration-300 transform group-hover:scale-105 group-hover:shadow-xl"
          />
        </button>
        {onShareRequest ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onShareRequest(book);
            }}
            aria-label={`Share ${book.book_name}`}
            className="absolute top-0 left-0 z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-violet-900 text-white shadow-md transition hover:bg-violet-950"
          >
            <ShareIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteRequest(book);
          }}
          aria-label={`Delete ${book.book_name}`}
          className="absolute top-0 right-0 z-10 flex h-7 w-7 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-red-600 text-white shadow-md transition hover:bg-red-700"
        >
          <TrashIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
        </button>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="mt-3 w-full cursor-pointer text-center"
      >
        <p className="text-sm font-medium text-gray-700 group-hover:text-violet-900 transition line-clamp-2 h-10">
          {book.book_name}
        </p>
        {published ? (
          <>
            <div className="w-full mt-2 h-2 bg-gray-300 rounded overflow-hidden">
              <div
                className="h-full bg-violet-800 transition-all"
                style={{
                  width: `${Math.min(100, Math.max(0, Number(book.progress) || 0))}%`,
                }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 text-center">
              {Math.round(Number(book.progress) || 0)}% read
            </p>
          </>
        ) : null}
      </button>
    </div>
  );
}

export default function AuthorHomePage() {
  usePageSeo({
    title: 'Author — Write & Publish Books',
    description: 'Write and publish your own books on Poppyruz. Create drafts, design covers, and share with readers.',
    path: '/author',
  });

  const { request, loading } = useApi();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [error, setError] = useState(null);
  const [deleteBookModal, setDeleteBookModal] = useState(null); // { book, published }
  const [shareBookModal, setShareBookModal] = useState(null); // book
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [deletingBookId, setDeletingBookId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const draftsRef = useRef(null);
  const publishedRef = useRef(null);

  const scrollLeft = (ref) => ref.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const scrollRight = (ref) => ref.current?.scrollBy({ left: 300, behavior: 'smooth' });

  const drafts = useMemo(
    () => books.filter((b) => !isPublishedBook(b)),
    [books],
  );
  const published = useMemo(() => books.filter(isPublishedBook), [books]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('sign_in_required');
      return;
    }
    (async () => {
      try {
        const data = await request({
          method: 'get',
          url: '/api/v1/pdf_parser/author/books/',
          withAuth: true,
        });
        setBooks(data.books || []);
      } catch (e) {
        setError('load_failed');
      }
    })();
  }, [request]);

  const openDeleteBookModal = useCallback((book, publishedBook) => {
    setDeleteBookModal({ book, published: publishedBook });
  }, []);

  const closeDeleteBookModal = () => setDeleteBookModal(null);

  const openShareBookModal = useCallback((book) => {
    setShareLinkCopied(false);
    setShareBookModal(book);
  }, []);

  const closeShareBookModal = () => {
    setShareBookModal(null);
    setShareLinkCopied(false);
  };

  const copyShareLink = async () => {
    if (!shareBookModal) return;
    const url = publishedBookShareUrl(shareBookModal.book_meta_unique_id);
    try {
      await navigator.clipboard.writeText(url);
      setShareLinkCopied(true);
    } catch {
      setShareLinkCopied(false);
    }
  };

  const executeDeleteBook = async () => {
    if (!deleteBookModal?.book || deletingBookId) return;
    const { book } = deleteBookModal;
    const bookId = book.book_meta_unique_id;
    setDeletingBookId(bookId);
    setError(null);
    try {
      await request({
        method: 'delete',
        url: `/api/v1/pdf_parser/author/books/${bookId}/`,
        withAuth: true,
      });
      setBooks((prev) => prev.filter((b) => b.book_meta_unique_id !== bookId));
      closeDeleteBookModal();
    } catch {
      setError('delete_failed');
      closeDeleteBookModal();
    } finally {
      setDeletingBookId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await request({
        method: 'post',
        url: '/api/v1/users/logout/',
        withAuth: true,
        data: { refresh: localStorage.getItem('refresh_token') },
      });
    } catch (e) {
      console.error('Logout API error:', e);
    }
    localStorage.clear();
    navigate('/');
  };

  const showLoadingList = loading && !books.length && error !== 'sign_in_required';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="relative z-50 bg-white shadow-md border-b">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="/poppyruz_icon.png"
              alt="Poppyruz"
              className="h-7 w-7 shrink-0"
              loading="eager"
              decoding="async"
              draggable="false"
            />
            <span className="text-xl font-bold text-gray-900">Poppyruz</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex flex-wrap items-center justify-end gap-4 md:gap-6">
            <Link
              to="/shop"
              aria-label="Shop"
              title="Shop"
              className="inline-flex items-center text-gray-700 hover:text-violet-900"
            >
              <ShoppingCartIcon className="h-6 w-6" aria-hidden />
            </Link>
            <Link
              to="/"
              className="text-base text-gray-800 hover:text-violet-900 cursor-pointer"
            >
              Home
            </Link>
            <Link to="/author/write/new" className={startNewBookButtonClass}>
              Start new book
            </Link>
            <div className="w-10 h-10 flex items-center cursor-pointer shrink-0">
              <ProfileDropdown onLogout={handleLogout} />
            </div>
          </div>

          {/* Mobile nav */}
          <div className="md:hidden flex items-center gap-1.5">
            <Link to="/shop" aria-label="Shop" title="Shop" className={authorMobileIconBtn}>
              <ShoppingCartIcon className="h-5 w-5" aria-hidden />
            </Link>
            <Link to="/author/write/new" className={`${startNewBookButtonClass} text-sm px-3 py-1.5`}>
              Start new book
            </Link>
            <button
              type="button"
              aria-label="Menu"
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="p-2 rounded border border-gray-200"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M3 6h18M3 12h18M3 18h18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {mobileMenuOpen ? (
          <div className="md:hidden border-t bg-white">
            <div className="px-4 py-3 grid gap-2 text-sm">
              <div className="border-b border-gray-100 pb-2 mb-1">
                <p className="font-medium truncate">{localStorage.getItem('name') || 'User'}</p>
                <p className="text-gray-500 text-xs mt-0.5 truncate">
                  {localStorage.getItem('email') || ''}
                </p>
              </div>
              <Link
                to="/shop"
                onClick={() => setMobileMenuOpen(false)}
                className="text-left hover:text-violet-900"
              >
                Shop
              </Link>
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  void handleLogout();
                }}
                className="text-left text-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        ) : null}
      </header>

      <div className="max-w-full mx-auto px-4 md:px-6 py-10">
        {error === 'sign_in_required' && (
          <p className="text-amber-700 mb-6 text-center">
            Please log in from the home page to use the author tools.
          </p>
        )}
        {error === 'load_failed' && (
          <p className="text-red-600 mb-6 text-center">Could not load your books.</p>
        )}
        {error === 'delete_failed' && (
          <p className="text-red-600 mb-6 text-center">Could not delete this book. Try again.</p>
        )}

        {showLoadingList ? (
          <div className="flex justify-center items-center py-24">
            <div className="w-10 h-10 border-4 border-violet-800 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Drafts */}
            <section className="relative px-2 md:px-6 py-2 max-w-full mb-6">
              <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Drafts</h2>
              {error === 'sign_in_required' ? null : drafts.length === 0 ? (
                <section className="relative w-screen max-w-[100vw] left-1/2 -translate-x-1/2 py-32 text-center text-gray-800 mb-5 overflow-hidden">
                  <div
                    className="absolute inset-0 bg-center bg-cover opacity-20"
                    style={{ backgroundImage: "url('/images/medievel_harbour.jpg')" }}
                  />
                  <div className="relative z-10 max-w-2xl mx-auto px-6">
                    <h2 className="text-4xl font-bold mb-4">Get Started</h2>
                    <p className="text-lg mb-6">You haven&apos;t started writing a book yet</p>
                    <div className="flex justify-center">
                      <Link to="/author/write/new" className={startNewBookButtonClass}>
                        Start new book
                      </Link>
                    </div>
                  </div>
                </section>
              ) : (
                <div className="relative">
                  {drafts.length > 7 && (
                    <>
                      <button
                        type="button"
                        onClick={() => scrollLeft(draftsRef)}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full hidden md:block"
                      >
                        <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollRight(draftsRef)}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full hidden md:block"
                      >
                        <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                      </button>
                    </>
                  )}
                  <div
                    ref={draftsRef}
                    className="flex flex-nowrap overflow-x-auto overflow-y-visible snap-x snap-mandatory scroll-smooth gap-6 px-4 md:px-8 pb-2 pt-4"
                  >
                    {drafts.map((book) => (
                      <AuthorBookCard
                        key={book.book_meta_unique_id}
                        book={book}
                        published={false}
                        onOpen={() => navigate(draftWriteUrl(book))}
                        onDeleteRequest={(b) => openDeleteBookModal(b, false)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Published */}
            <section className="relative px-2 md:px-6 py-8 max-w-full mb-10">
              <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Published</h2>
              {published.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No published books yet. Finish writing a book and publish, your audience are waiting!</p>
              ) : (
                <div className="relative">
                  {published.length > 7 && (
                    <>
                      <button
                        type="button"
                        onClick={() => scrollLeft(publishedRef)}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full hidden md:block"
                      >
                        <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollRight(publishedRef)}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full hidden md:block"
                      >
                        <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                      </button>
                    </>
                  )}
                  <div
                    ref={publishedRef}
                    className="flex flex-nowrap overflow-x-auto overflow-y-visible snap-x snap-mandatory scroll-smooth gap-6 px-4 md:px-8 pb-2 pt-4"
                  >
                    {published.map((book) => (
                      <AuthorBookCard
                        key={book.book_meta_unique_id}
                        book={book}
                        published
                        onOpen={() => navigate(`/read_authored/${book.book_meta_unique_id}`)}
                        onDeleteRequest={(b) => openDeleteBookModal(b, true)}
                        onShareRequest={openShareBookModal}
                      />
                    ))}
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <NativeAffiliateBanner placement="author_home" />

      {shareBookModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-author-book-title"
            className="w-[min(100%,28rem)] space-y-4 rounded-lg bg-white p-6 shadow-lg"
          >
            <h2 id="share-author-book-title" className="text-lg font-semibold text-slate-900">
              Share &ldquo;{shareBookModal.book_name}&rdquo;
            </h2>
            <p className="text-sm text-slate-600">
              Anyone with this link can read your published book.
            </p>
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-mono text-slate-800">
                {publishedBookShareUrl(shareBookModal.book_meta_unique_id)}
              </span>
              <button
                type="button"
                onClick={() => void copyShareLink()}
                aria-label="Copy link"
                className="shrink-0 rounded-md p-1.5 text-violet-900 transition hover:bg-violet-50 hover:text-violet-950"
              >
                <ClipboardDocumentIcon className="h-5 w-5" aria-hidden />
              </button>
            </div>
            {shareLinkCopied ? (
              <p className="text-sm text-green-600">Link copied to clipboard.</p>
            ) : null}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeShareBookModal}
                className="rounded bg-gray-300 px-4 py-2 hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteBookModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-author-book-title"
            className="w-[min(100%,22rem)] space-y-4 rounded-lg bg-white p-6 text-center shadow-lg"
          >
            <h2 id="delete-author-book-title" className="text-lg font-semibold text-slate-900">
              Delete &ldquo;{deleteBookModal.book.book_name}&rdquo;?
            </h2>
            <p className="text-sm text-slate-600">
              {deleteBookModal.published
                ? 'This will permanently delete the book and revert its publication. Readers will no longer see it. This cannot be undone.'
                : 'This will permanently delete the draft and all of its pages. This cannot be undone.'}
            </p>
            <div className="mt-4 flex justify-center gap-4">
              <button
                type="button"
                onClick={() => void executeDeleteBook()}
                disabled={deletingBookId !== null}
                className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingBookId ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button
                type="button"
                onClick={closeDeleteBookModal}
                disabled={deletingBookId !== null}
                className="rounded bg-gray-300 px-4 py-2 hover:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
